import type { AnalyticsEvent, AnalyticsSink } from "./types.js";

export interface Ga4SinkConfig {
  measurementId: string;
}

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: Gtag;
    dataLayer?: unknown[];
  }
}

/**
 * Sends events to Google Analytics 4 via the standard `gtag.js` global -
 * "integrate with GA4 only where configured," per the brief, so this sink
 * does nothing (never throws, never sends anything) unless a
 * `measurementId` was actually supplied and the `gtag.js` loader has run
 * (see `loadGa4Script`). Event properties have already been through
 * `scrubEventProperties` by the time they reach here (see
 * AnalyticsClient) - this sink does not re-implement that check, but it
 * also never adds anything to the properties it was handed.
 */
export class Ga4AnalyticsSink implements AnalyticsSink {
  constructor(private readonly config: Ga4SinkConfig) {}

  track(event: AnalyticsEvent): void {
    if (!this.config.measurementId) return;
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    try {
      window.gtag("event", event.name, { ...event.properties });
    } catch {
      // A destination failure must never break the estimator itself.
    }
  }
}

/**
 * Injects the standard GA4 `gtag.js` loader script, once. Callers must
 * only invoke this after analytics consent has actually been granted -
 * this function does not check consent itself, since it has no access to
 * consent state; see estimator-app's analyticsClient.ts for the call
 * site that gates this behind `hasAnalyticsConsent`.
 *
 * Sets `anonymize_ip: true` and disables Google Signals/ad personalization
 * - this is an anonymous-usage-analytics integration, not a marketing/ads
 * one, and nothing about that should change without a deliberate,
 * documented decision (see docs/privacy-security.md).
 */
export function loadGa4Script(measurementId: string): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  if (!measurementId) return;
  if (document.getElementById("ga4-loader-script")) return; // already loaded

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false
  });

  const script = document.createElement("script");
  script.id = "ga4-loader-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}
